import React, { PropsWithChildren, useState } from 'react';
import { Card, Collapsible, Text, cn } from 'shared-components';
import { LoanTimeline } from './LoanTimeline';
import { BCMShortCloseButton } from '@components/BCMShort/WizardShell';
import type { FaqProps } from './loanFaqs';

interface LoanWizardShellProps {
  /** 0..N — drives the loan-flow phase highlight in the timeline. */
  currentStepIndex: number;
  /** When true, hides the timeline + close button and switches the card to the animated gradient. */
  isSubmitting?: boolean;
  /** Hides the timeline (e.g. for a standalone landing page). */
  hideTimeline?: boolean;
  /** Override the small label shown in the card chip (top of the card). */
  brandLabel?: string;
  /** Use the wider content area (max-w-[920px]) for content-dense pages like the summary step. */
  wide?: boolean;
  /** When provided, shows a `?`/`X` toggle in the top-right corner that opens a separate FAQ card in place of the children. */
  faq?: FaqProps;
}

const DEFAULT_BRAND_LABEL = 'FINANCING APPLICATION';

// Outer chrome for every loan-flow step. Visually identical to
// BCMShortWizardShell but uses the loan timeline + a loan-flow brand
// label. Kept as a sibling rather than a refactor of BCMShortWizardShell
// so the two flows can evolve independently if needed.
export const LoanWizardShell: React.FC<PropsWithChildren<LoanWizardShellProps>> = ({
  currentStepIndex,
  isSubmitting = false,
  hideTimeline = false,
  brandLabel = DEFAULT_BRAND_LABEL,
  wide = false,
  faq,
  children,
}) => {
  const [isFaqOpen, setIsFaqOpen] = useState(false);
  const showTimeline = !isSubmitting && !hideTimeline;
  const showFaqToggle = !isSubmitting && !!faq;

  return (
    <div className="min-h-screen p-[10px] relative z-10 w-full">
      <div
        className={cn(
          'border border-[#d5cec8] pt-[20px] rounded-[15px] shadow-[0px_0px_20px_0px_rgba(13,13,13,0.05)] w-full min-h-[calc(100vh-20px)] w640:h-[calc(100vh-20px)] w640:max-h-[calc(100vh-20px)] w640:overflow-y-auto no-scrollbar flex flex-col relative',
          {
            'bg-v2-card-gradient-to-scroll bg-[length:100%_200%] animate-scrollBgGradient': isSubmitting,
          }
        )}
        style={
          !isSubmitting ? { background: 'linear-gradient(180deg, #fff6f0 0%, #fff6f0 50%, #D5D973 100%)' } : undefined
        }
      >
        {showTimeline && <LoanTimeline currentStep={currentStepIndex} />}

        <div className="flex gap-[10px] items-center px-[10px] py-[10px] mx-[20px]">
          <div className="flex flex-1 gap-[5px] h-[14px] items-center overflow-clip">
            <div className="w-[10px] h-[10px] rounded-full bg-[#d5d973]" />
            <span className="font-condensed text-[12px] text-[#9e9e9e] uppercase leading-[1.2]">{brandLabel}</span>
          </div>
          {showFaqToggle && (
            <button
              type="button"
              onClick={() => setIsFaqOpen((open) => !open)}
              aria-label={isFaqOpen ? 'Close FAQ' : 'Open FAQ'}
              aria-expanded={isFaqOpen}
              className="relative flex items-center justify-center w-[20px] h-[20px] rounded-full border border-[#9e9e9e] bg-transparent text-[#9e9e9e] font-condensed text-[12px] leading-none hover:border-charcoal hover:text-charcoal transition-colors after:absolute after:-top-[15px] after:-bottom-[11px] after:-left-[20px] after:-right-[6px] after:content-['']"
            >
              {isFaqOpen ? 'X' : '?'}
            </button>
          )}
          {!isSubmitting && !isFaqOpen && <BCMShortCloseButton />}
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-[20px] pb-[40px] relative">
          <div
            className={cn('w-full flex flex-col items-center relative z-10', wide ? 'max-w-[920px]' : 'max-w-[592px]')}
          >
            {/* Children stay mounted so step state (e.g. owner count, react-hook-form
                values) survives a FAQ open/close toggle. We just hide them visually. */}
            <div className={cn('w-full flex flex-col items-center', { hidden: isFaqOpen && !!faq })}>{children}</div>
            {isFaqOpen && faq && <FaqCard faq={faq} />}
          </div>
        </div>
      </div>
    </div>
  );
};

const FaqCard: React.FC<{ faq: FaqProps }> = ({ faq }) => (
  <Card size="fullwidth" className="!bg-dawn !text-charcoal text-left !px-[20px] !py-[30px]">
    <Text variant="headline-lg" className="pb-[30px] pt-[10px]">
      {faq.heading ?? 'Frequently Asked Questions'}
    </Text>
    {faq.questions?.map((q, idx) => (
      <Collapsible key={idx} heading={q.question}>
        <Text variant="muted">{q.answer}</Text>
      </Collapsible>
    ))}
  </Card>
);
